import React, { useState } from 'react';
/**
 * `CardDateInput` renders a two-field MM/YY card expiry input.
 *
 * Behaviour:
 * - When 2 characters are entered in the month field, focus is moved to the
 *   year field.
 * - When an `error` is provided, an error styling class is applied and the
 *   message is rendered below the inputs.
 * - Selection on focus is enabled to simplify overwriting existing values.
 *
 * State is owned by the parent; this component reports changes via
 * `onChange(e, key)` where `key` is either `month` or `year`.
 */
import TextInput from '../TextInput/TextInput';
import './CardDateInput.scss';

type CardDateProps = {
  label: string;
  ariaLabel?: string;
  month: string;
  year: string;
  error: string;
  onChange: (e: React.FormEvent<HTMLInputElement>, key: string) => void;
};

export const CardDateInput = ({
  label,
  ariaLabel,
  month,
  year,
  error,
  onChange
}: CardDateProps): JSX.Element => {
  const [focusedElement, setFocusedElement] = useState<null | string>(null);
  const expiryGroupLabel = ariaLabel || label || 'Expiry date';

  const getCardDateLabelPanel = () => {
    if (label && label.length > 0) {
      return <div className='d-block'>{label}</div>;
    }
  };

  const onChangeValue = (
    e: React.FormEvent<HTMLInputElement>,
    value: string
  ) => {
    onChange(e, value);

    if (e.currentTarget.value.length < 2) return;

    switch (e.currentTarget.name) {
      case 'month':
        setFocusedElement('year');
        break;
      default:
        setFocusedElement(null);
        break;
    }
  };

  const onLeave = () => {
    setFocusedElement(null);
  };

  const onFocus = (e: React.FormEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  const getCardDateInputPanel = () => {
    let inputClasses = '';
    if (error && error.length > 0) {
      inputClasses = 'has-error';
    }

    return (
      <div className={inputClasses}>
        <div className='d-inline-block me-1 card-date'>
          <div className='control-label mt-0'>MM</div>
          <TextInput
            className='input-text'
            placeholder='MM'
            name='month'
            ariaLabel={`${expiryGroupLabel} month`}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              onChangeValue(e, 'month')
            }
            value={month}
            maxLength={2}
            focus={focusedElement === 'month'}
            onLeave={onLeave}
            onFocus={onFocus}
          />
        </div>
        <div className='d-inline-block me-1 card-date'>
          <div className='control-label mt-0'>YY</div>
          <TextInput
            className='input-text'
            placeholder='YY'
            name='year'
            ariaLabel={`${expiryGroupLabel} year`}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              onChangeValue(e, 'year')
            }
            value={year}
            maxLength={2}
            focus={focusedElement === 'year'}
            onLeave={onLeave}
            onFocus={onFocus}
          />
        </div>
      </div>
    );
  };

  const getCardDateErrorPanel = () => {
    if (error && error.length > 0) {
      return (
        <div className='has-error'>
          <div className='input error-margin'>{error}</div>
        </div>
      );
    }
  };

  const labelPanel = getCardDateLabelPanel();
  const inputPanel = getCardDateInputPanel();
  const errorPanel = getCardDateErrorPanel();

  return (
    <div role='group' aria-label={expiryGroupLabel}>
      {labelPanel}
      {inputPanel}
      {errorPanel}
    </div>
  );
};
